import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Camera, CheckCircle, Warning, ArrowLeft, ArrowRight, House, Play,
  PaintBrush, CurrencyDollar, Plus, Wrench, FloppyDisk, Wind, Trash, ClipboardText, Fire, FilePdf, Key, ArrowUUpLeft,
} from "@phosphor-icons/react";
import VoiceRecorder from "@/components/VoiceRecorder";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import InspectRadarGrid from "@/components/InspectRadarGrid";
import { useSimpleMode } from "@/lib/simpleMode";

// Collapsible sub-checks (Windows / Screens / Blinds). In Simple Mode the whole
// section starts collapsed behind a "+ Rate windows / screens / blinds" tap so
// new techs aren't overwhelmed. Veterans can expand and rate as before.
function SubChecksSection({ subChecks, setSubChecks, completed }) {
  const { simpleMode } = useSimpleMode();
  const [expanded, setExpanded] = useState(!simpleMode);
  const anyRated = ["windows", "screens", "blinds"].some(k => subChecks[`${k}_condition`]);
  if (simpleMode && !expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} data-testid="insp-sub-checks-expand"
        className="w-full h-11 px-3 border-2 border-dashed border-slate-300 bg-white text-[11px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 flex items-center justify-between">
        <span>+ Rate Windows · Screens · Blinds{anyRated ? " (some rated)" : ""}</span>
        <span className="text-slate-400">Optional</span>
      </button>
    );
  }
  return (
    <div className="p-3 border-2 border-slate-300 bg-slate-50 space-y-3" data-testid="insp-sub-checks">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-black uppercase tracking-widest text-slate-600">Windows · Screens · Blinds</div>
        {simpleMode && (
          <button type="button" onClick={() => setExpanded(false)} data-testid="insp-sub-checks-collapse"
            className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a]">Hide</button>
        )}
      </div>
      {[
        { key: "windows", label: "Windows" },
        { key: "screens", label: "Screens" },
        { key: "blinds", label: "Blinds" },
      ].map(({ key, label }) => (
        <div key={key} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
            <select
              value={subChecks[`${key}_condition`]}
              onChange={e => setSubChecks(prev => ({ ...prev, [`${key}_condition`]: e.target.value }))}
              disabled={completed}
              data-testid={`insp-sub-${key}-condition`}
              className="h-8 px-2 border-2 border-[#0f172a] bg-white text-xs font-bold">
              <option value="">— Rate —</option>
              <option value="great">Great</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="broken">Broken</option>
              <option value="na">N/A</option>
            </select>
          </div>
          <input
            value={subChecks[`${key}_notes`]}
            onChange={e => setSubChecks(prev => ({ ...prev, [`${key}_notes`]: e.target.value }))}
            disabled={completed}
            data-testid={`insp-sub-${key}-notes`}
            placeholder={`${label} notes (optional)`}
            className="w-full h-9 px-2 border-2 border-[#0f172a] text-xs"/>
        </div>
      ))}
      <p className="text-[10px] text-slate-500 font-medium">
        Rated separately so the office can flag replacement items on the turn report.
      </p>
    </div>
  );
}

const CONDITIONS = [
  { key: "great", label: "Great", color: "bg-emerald-600" },
  { key: "good", label: "Good", color: "bg-lime-600" },
  { key: "fair", label: "Fair", color: "bg-amber-500" },
  { key: "poor", label: "Poor", color: "bg-orange-600" },
  { key: "broken", label: "Broken", color: "bg-red-600" },
  { key: "na", label: "N/A", color: "bg-slate-500" },
];

const REQUIRES_NOTES = new Set(["fair", "poor", "broken"]);

// Turn Ballpark — rough cost estimate per room condition. Matches the
// backend heuristic in `routes/inspections.py:turn_ballpark`.
const BALLPARK_PER_ROOM = { great: 0, good: 0, na: 0, fair: 150, poor: 450, broken: 950 };
const BALLPARK_SUBCHECK = { fair: 40, poor: 110, broken: 220 };

function computeTurnBallpark(rooms) {
  let total = 0;
  let rated = 0;
  const worstRooms = [];
  for (const r of (rooms || [])) {
    const cond = (r.condition || "").toLowerCase();
    if (cond in BALLPARK_PER_ROOM) {
      rated += 1;
      const base = BALLPARK_PER_ROOM[cond];
      const sub = r.sub_checks || {};
      let subCost = 0;
      ["windows", "screens", "blinds"].forEach(k => {
        const sc = (sub[`${k}_condition`] || "").toLowerCase();
        subCost += BALLPARK_SUBCHECK[sc] || 0;
      });
      const line = base + subCost;
      if (line > 0) worstRooms.push({ room: r.room || r.name, cost: line, condition: cond });
      total += line;
    }
  }
  const unrated = (rooms || []).length - rated;
  const bucket =
    total < 500 ? "under $500" :
    total < 1500 ? "$500–$1.5k" :
    total < 3500 ? "$1.5k–$3.5k" :
    total < 7500 ? "$3.5k–$7.5k" :
    "$7.5k+";
  const confidence = unrated === 0 && total >= 0 ? "high" : unrated <= 2 ? "medium" : "low";
  worstRooms.sort((a, b) => b.cost - a.cost);
  return { total, bucket, confidence, rated, unrated, worst: worstRooms.slice(0, 3) };
}


export default function InspectionWizard({ jobId, phase, onClose, onJobReload }) {
  const [state, setState] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [annotate, setAnnotate] = useState(null); // { path, file_id }
  const [applianceOpen, setApplianceOpen] = useState(false);
  const [meterOpen, setMeterOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [propertyCfg, setPropertyCfg] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { path, isVideo }
  const [viewMode, setViewMode] = useState("room"); // "room" | "property" | "paint" | "grid"

  const finalize = async () => {
    if (job?.status === "completed") {
      window.location.href = "/admin/completed-inspections";
      return;
    }
    const total = state?.summary?.total || 0;
    const done = state?.summary?.completed || 0;
    let force = false;
    if (total > 0 && done < total) {
      if (!window.confirm(`Only ${done}/${total} rooms are complete. Finalize anyway and push to Completed Reports?`)) return;
      force = true;
    } else if (!window.confirm("Finalize this inspection and save to Completed Reports? You'll still be able to view/download the PDF from the Reports list.")) {
      return;
    }
    setFinalizing(true);
    try {
      const url = `/jobs/${jobId}/finalize-inspection${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      toast.success(data.already_completed ? "Already finalized — opening Reports" : "Inspection saved to Reports");
      if (onJobReload) await onJobReload();
      // Give the toast a moment to render, then navigate
      setTimeout(() => { window.location.href = "/admin/completed-inspections"; }, 500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not finalize");
    } finally { setFinalizing(false); }
  };

  const load = async () => {
    const [insp, jobResp] = await Promise.all([
      api.get(`/jobs/${jobId}/inspection/${phase}`),
      api.get(`/jobs/${jobId}`),
    ]);
    setState(insp.data);
    setJob(jobResp.data);
    if (jobResp.data?.property_address) {
      try {
        const cfg = await api.get(`/property-config?address=${encodeURIComponent(jobResp.data.property_address)}`);
        setPropertyCfg(cfg.data);
      } catch (_) { setPropertyCfg({ found: false, maintenance: {} }); }
    }
    if (!activeRoom && insp.data.rooms.length > 0) {
      const firstIncomplete = insp.data.rooms.find(x => !x.completed) || insp.data.rooms[0];
      setActiveRoom(firstIncomplete.room);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [jobId, phase]);

  if (!state) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4">
        <div className="p-6 bg-white border-2 border-[#0f172a] font-bold">Loading inspection…</div>
      </div>
    );
  }

  const rooms = state.rooms || [];
  // Merge inspection photos into each room so the Grid can show per-trade
  // photo counts. Photos live on the job (before_photos / after_photos)
  // and carry `inspection_room` + `inspection_trade` tags.
  const photoField = phase === "move_out" ? "before_photos" : "after_photos";
  const roomPhotosByName = (job?.[photoField] || []).reduce((acc, p) => {
    if (!p.inspection_room) return acc;
    (acc[p.inspection_room] = acc[p.inspection_room] || []).push(p);
    return acc;
  }, {});
  const roomsEnriched = rooms.map(r => ({ ...r, _photos: roomPhotosByName[r.room] || [] }));
  const current = rooms.find(r => r.room === activeRoom) || rooms[0];
  const summary = state.summary || {};
  const phaseTitle = phase === "move_out" ? "InspectRadar™ · Move-Out Sweep" : "InspectRadar™ · Move-In Sweep";
  const phaseSub = phase === "move_out"
    ? "Documentation only — condition rating, photos & serials. Repair / cleaning tasks live on the Checklist tab."
    : "Post-work walk-through — verify condition and capture final photos.";
  const phaseColor = phase === "move_out" ? "border-red-600" : "border-emerald-600";

  const saveRoom = async (patch, opts = { toastMsg: null, keepOpen: false, advance: false }) => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ room: current.room });
      await api.patch(`/jobs/${jobId}/inspection/${phase}/room?${params.toString()}`, patch);
      if (opts.toastMsg) toast.success(opts.toastMsg);
      await load();
      if (onJobReload) await onJobReload();
      if (opts.advance) {
        const idx = rooms.findIndex(r => r.room === current.room);
        const next = rooms.slice(idx + 1).find(r => !r.completed);
        if (next) setActiveRoom(next.room);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const flagPhoto = async (photo, next) => {
    try {
      await api.patch(`/jobs/${jobId}/photo/${photo.file_id}/chargeable`, {
        chargeable_to_tenant: next,
        damage_description: photo.damage_description || "",
      });
      toast.success(next ? "Flagged as tenant damage" : "Flag cleared");
      await load();
      if (onJobReload) await onJobReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update flag");
    }
  };

  const applianceCount = (job?.appliances || []).length;

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-stretch sm:items-center justify-center sm:p-4" data-testid="inspection-wizard">
      <div className={`w-full sm:max-w-4xl bg-white border-2 sm:border-4 ${phaseColor} sm:shadow-[8px_8px_0px_#0f172a] flex flex-col max-h-full`}>
        <div className="flex items-start sm:items-center justify-between gap-2 p-3 sm:p-4 border-b-2 border-[#0f172a] bg-[#0f172a] text-white">
          <div className="min-w-0 flex-1">
            <h2 className="font-display font-black text-base sm:text-2xl uppercase tracking-tight truncate">{phaseTitle}</h2>
            <div className="hidden sm:block text-[11px] font-medium text-slate-300 mt-0.5 max-w-md">{phaseSub}</div>
            <div className="text-[10px] sm:text-xs font-bold text-slate-300 uppercase tracking-wider mt-1 flex items-center gap-2 flex-wrap">
              <span>{summary.completed}/{summary.total} rooms · {applianceCount} appliance{applianceCount === 1 ? "" : "s"}</span>
              <TurnBallparkChip rooms={rooms}/>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <a
              href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/jobs/${jobId}/damage-report-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
              target="_blank" rel="noreferrer"
              data-testid="insp-damage-report-btn"
              title="Damage Report PDF"
              className="h-10 w-10 sm:w-auto sm:px-3 border-2 border-red-400 bg-red-600 text-white flex items-center justify-center sm:gap-1 hover:bg-red-700 transition-colors font-black uppercase text-[11px] tracking-wider">
              <FilePdf weight="fill" size={14}/><span className="hidden sm:inline">Damage Report</span>
            </a>
            <button onClick={() => setApplianceOpen(true)} data-testid="insp-open-appliance-btn"
              title="Log Appliance"
              className="h-10 w-10 sm:w-auto sm:px-3 border-2 border-white flex items-center justify-center sm:gap-1 hover:bg-white hover:text-[#0f172a] transition-colors font-black uppercase text-[11px] tracking-wider">
              <Wrench weight="bold" size={14}/><span className="hidden sm:inline">Log Appliance</span>
            </button>
            <button onClick={finalize} disabled={finalizing || job?.status === "completed"} data-testid="insp-finalize-btn"
              title={job?.status === "completed" ? "Already finalized — see Completed Reports" : "Finalize & Save to Reports"}
              className={`h-10 sm:w-auto sm:px-3 border-2 flex items-center justify-center sm:gap-1.5 font-black uppercase text-[11px] tracking-wider transition-colors ${
                job?.status === "completed"
                  ? "border-emerald-300 bg-emerald-800 text-emerald-100 cursor-default"
                  : (summary.total > 0 && summary.completed === summary.total
                      ? "border-emerald-300 bg-emerald-500 text-white hover:bg-emerald-600 animate-pulse-slow px-3"
                      : "border-emerald-300 bg-emerald-700/60 text-white hover:bg-emerald-700 px-3")
              }`}>
              <CheckCircle weight="fill" size={14}/>
              <span className="hidden sm:inline">
                {job?.status === "completed" ? "Finalized ✓" : (finalizing ? "Finalizing…" : "Finalize & View Report")}
              </span>
            </button>
            <button onClick={onClose} data-testid="inspection-close-btn"
              title="Close"
              className="h-10 w-10 sm:w-auto sm:px-3 border-2 border-white flex items-center justify-center sm:gap-1 hover:bg-white hover:text-[#0f172a] transition-colors shrink-0 font-black uppercase text-[11px] tracking-wider">
              <X weight="bold" size={20}/><span className="hidden sm:inline">Close</span>
            </button>
          </div>
        </div>

        <div className="h-2 bg-slate-200 relative">
          <div className={`h-full ${phase === "move_out" ? "bg-red-600" : "bg-emerald-600"} transition-all`}
            style={{ width: summary.total ? `${(summary.completed / summary.total) * 100}%` : "0%" }}/>
        </div>

        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
          <div className="sm:w-56 flex-shrink-0 border-b-2 sm:border-b-0 sm:border-r-2 border-[#0f172a] bg-slate-50 overflow-y-auto max-h-40 sm:max-h-full">
            <div className="p-2 sm:p-3 flex sm:flex-col gap-1.5 overflow-x-auto sm:overflow-x-visible">
              <button onClick={() => setViewMode("grid")} data-testid="insp-grid-tab-btn"
                className={`text-left px-3 py-2 border-2 whitespace-nowrap sm:whitespace-normal flex items-center gap-2 min-w-max sm:min-w-0 ${
                  viewMode === "grid"
                    ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                    : "bg-fuchsia-50 border-fuchsia-500 text-fuchsia-900 hover:bg-fuchsia-100"
                }`}>
                <ClipboardText weight="bold" size={16} className="flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-xs uppercase tracking-wider">Grid View</div>
                  <div className="text-[9px] font-bold opacity-80 mt-0.5">Fast rate · Rooms × Trades</div>
                </div>
              </button>
              <button onClick={() => setViewMode("property")} data-testid="insp-property-tab-btn"
                className={`text-left px-3 py-2 border-2 whitespace-nowrap sm:whitespace-normal flex items-center gap-2 min-w-max sm:min-w-0 ${
                  viewMode === "property"
                    ? "bg-amber-500 border-amber-500 text-white"
                    : "bg-amber-50 border-amber-500 text-amber-900 hover:bg-amber-100"
                }`}>
                <Wind weight="bold" size={16} className="flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-xs uppercase tracking-wider">Property & Recs</div>
                  <div className="text-[9px] font-bold opacity-80 mt-0.5">Filter · Maintenance · PM</div>
                </div>
              </button>
              <button onClick={() => setViewMode("paint")} data-testid="insp-paint-tab-btn"
                className={`text-left px-3 py-2 border-2 whitespace-nowrap sm:whitespace-normal flex items-center gap-2 min-w-max sm:min-w-0 ${
                  viewMode === "paint"
                    ? "bg-violet-600 border-violet-600 text-white"
                    : "bg-violet-50 border-violet-500 text-violet-900 hover:bg-violet-100"
                }`}>
                <PaintBrush weight="bold" size={16} className="flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-xs uppercase tracking-wider">Paint Recs</div>
                  <div className="text-[9px] font-bold opacity-80 mt-0.5">Ceilings · Trim · Walls</div>
                </div>
              </button>
              {rooms.length === 0 && (
                <div className="p-3 text-xs text-slate-500 font-bold">
                  No rooms configured on this Turn job. Edit the job and set the room config first.
                </div>
              )}
              {rooms.map((r) => {
                const isActive = r.room === current?.room;
                const isDone = r.completed;
                const cond = CONDITIONS.find(c => c.key === r.condition);
                return (
                  <button key={r.room} onClick={() => { setActiveRoom(r.room); setViewMode("room"); }}
                    data-testid={`insp-room-btn-${r.room}`}
                    className={`text-left px-3 py-2 border-2 border-[#0f172a] whitespace-nowrap sm:whitespace-normal flex items-center gap-2 min-w-max sm:min-w-0 ${
                      viewMode === "room" && isActive ? "bg-[#0f172a] text-white" : (isDone ? "bg-emerald-50" : "bg-white")
                    }`}>
                    <span className={`h-5 w-5 border-2 flex-shrink-0 flex items-center justify-center ${
                      isDone ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-400"
                    }`}>
                      {isDone && <CheckCircle weight="fill" size={14}/>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-xs uppercase tracking-wider">{r.room}</div>
                      {cond && !isActive && (
                        <div className={`text-[9px] font-black uppercase mt-0.5 ${cond.color.replace("bg-", "text-")}`}>
                          {cond.label}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 p-0 sm:p-0 overflow-y-auto">
            {viewMode === "grid" ? (
              <InspectRadarGrid
                jobId={jobId}
                phase={phase}
                rooms={roomsEnriched}
                onRoomsChanged={async () => { await load(); if (onJobReload) await onJobReload(); }}
                isSaving={saving}
              />
            ) : (
              <div className="p-4 sm:p-6">
                {viewMode === "property" ? (
                  <PropertyPanel
                    jobId={jobId}
                    address={job?.property_address || ""}
                    propertyCfg={propertyCfg}
                    recommendations={job?.service_recommendations || []}
                    onReload={load}
                    onJobReload={onJobReload}
                    onOpenMeter={() => setMeterOpen(true)}
                    job={job}
                    phase={phase}
                  />
                ) : viewMode === "paint" ? (
                  <PaintPanel
                    jobId={jobId}
                    rooms={rooms}
                    paintRec={job?.paint_recommendation}
                    onSaved={async () => { await load(); if (onJobReload) await onJobReload(); }}
                  />
                ) : current ? (
                  <RoomEditor
                    key={current.room}
                    room={current}
                    phase={phase}
                    jobId={jobId}
                    saving={saving}
                    onSave={saveRoom}
                    onReload={load}
                    onAnnotate={(p) => setAnnotate(p)}
                    onFlag={flagPhoto}
                    onLogAppliance={() => setApplianceOpen(true)}
                    onOpenLightbox={(p) => setLightbox(p)}
                  />
                ) : (
                  <div className="text-center py-12 text-slate-500 font-bold">Select a room to begin.</div>
                )}
              </div>
            )}
          </div>
        </div>

        {current && viewMode === "room" && (
          <div className="p-3 border-t-2 border-[#0f172a] bg-white flex items-center justify-between gap-2">
            <button
              onClick={() => {
                const idx = rooms.findIndex(r => r.room === current.room);
                if (idx > 0) setActiveRoom(rooms[idx - 1].room);
              }}
              disabled={rooms.findIndex(r => r.room === current.room) <= 0}
              data-testid="insp-prev-btn"
              className="h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 disabled:opacity-40">
              <ArrowLeft weight="bold" size={14}/> Prev
            </button>
            <div className="text-xs font-black uppercase text-slate-500 tracking-wider text-center">
              Room {rooms.findIndex(r => r.room === current.room) + 1} of {rooms.length}
            </div>
            <button
              onClick={() => {
                const idx = rooms.findIndex(r => r.room === current.room);
                if (idx < rooms.length - 1) setActiveRoom(rooms[idx + 1].room);
              }}
              disabled={rooms.findIndex(r => r.room === current.room) >= rooms.length - 1}
              data-testid="insp-next-btn"
              className="h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 disabled:opacity-40">
              Next <ArrowRight weight="bold" size={14}/>
            </button>
          </div>
        )}
      </div>

      {annotate && (
        <PhotoAnnotator
          imagePath={annotate.path}
          onClose={() => setAnnotate(null)}
          onSave={async (file) => {
            try {
              const fd = new FormData();
              fd.append("room", current.room);
              fd.append("file", file);
              await api.post(`/jobs/${jobId}/inspection/${phase}/room/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
              toast.success("Annotated photo saved");
              await load();
              if (onJobReload) await onJobReload();
            } catch (e) {
              toast.error("Could not save annotated photo");
            }
            setAnnotate(null);
          }}
        />
      )}

      {applianceOpen && (
        <ApplianceLogModal
          jobId={jobId}
          defaultLocation={current?.room || ""}
          onClose={() => setApplianceOpen(false)}
          onSaved={async () => { setApplianceOpen(false); await load(); if (onJobReload) await onJobReload(); }}
        />
      )}

      {meterOpen && (
        <MeterLogModal
          address={job?.property_address || ""}
          onClose={() => setMeterOpen(false)}
          onSaved={async () => { setMeterOpen(false); await load(); if (onJobReload) await onJobReload(); }}
        />
      )}
      {lightbox && (
        <div className="fixed inset-0 z-[120] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="insp-lightbox">
          <button onClick={() => setLightbox(null)} data-testid="insp-lightbox-close"
            className="absolute top-4 right-4 h-12 w-12 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center border-2 border-white/50 z-10">
            <X weight="bold" size={26}/>
          </button>
          {lightbox.isVideo ? (
            <video src={fileUrl(lightbox.path)} controls autoPlay className="max-w-full max-h-full" onClick={(e) => e.stopPropagation()}/>
          ) : (
            <img src={fileUrl(lightbox.path)} alt="" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()}/>
          )}
        </div>
      )}
    </div>
  );
}

function RoomEditor({ room, phase, jobId, saving, onSave, onReload, onAnnotate, onFlag, onLogAppliance, onOpenLightbox }) {
  const [condition, setCondition] = useState(room.condition || "");
  const [notes, setNotes] = useState(room.notes || "");
  const initialSc = room.sub_checks || {};
  const [subChecks, setSubChecks] = useState({
    windows_condition: initialSc.windows_condition || "",
    screens_condition: initialSc.screens_condition || "",
    blinds_condition: initialSc.blinds_condition || "",
    windows_notes: initialSc.windows_notes || "",
    screens_notes: initialSc.screens_notes || "",
    blinds_notes: initialSc.blinds_notes || "",
  });
  const cameraRef = useRef();
  const galleryRef = useRef();
  const [uploading, setUploading] = useState(false);
  // Track photos that failed all retries so the tech can see & retry them.
  // Each entry: { id, file, name, previewUrl, attempts, lastError }
  const [failedUploads, setFailedUploads] = useState([]);
  const notesRequired = REQUIRES_NOTES.has(condition);
  const isNA = condition === "na";
  const hasPhoto = (room.photos || []).length > 0;
  const canComplete = !!condition && (isNA || (hasPhoto && (!notesRequired || (notes || "").trim().length >= 3)));
  const missingReason = !condition
    ? "Pick a condition rating"
    : isNA
      ? null
      : (!hasPhoto ? "Add at least one photo" : (notesRequired && (notes || "").trim().length < 3 ? "Notes required for Fair / Poor / Broken" : null));

  // Single-file upload with retries + exponential backoff. Never eats the exception —
  // caller checks resolved value to decide whether to enqueue for manual retry.
  const uploadPhotoWithRetry = async (file, attempts = 3) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        const fd = new FormData();
        fd.append("room", room.room);
        fd.append("file", file);
        await api.post(`/jobs/${jobId}/inspection/${phase}/room/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 45000,
        });
        return { ok: true, attempts: i + 1 };
      } catch (e) {
        lastErr = e;
        // Don't retry 4xx client errors (invalid file / permissions) — those won't resolve on retry.
        const st = e?.response?.status;
        if (st && st >= 400 && st < 500 && st !== 408 && st !== 429) break;
        // Exponential backoff: 500ms, 1500ms, 4500ms
        if (i < attempts - 1) await new Promise(r => setTimeout(r, 500 * Math.pow(3, i)));
      }
    }
    return { ok: false, attempts, error: lastErr?.response?.data?.detail || lastErr?.message || "Upload failed" };
  };

  const uploadPhoto = async (file) => uploadPhotoWithRetry(file, 3);

  const uploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setUploading(true);
    let ok = 0;
    const newFailed = [];
    const tid = arr.length > 1 ? toast.loading(`Uploading ${arr.length} photos…`) : null;
    const CONCURRENCY = 3;
    const queue = arr.map((f, idx) => ({ f, idx }));
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const { f } = queue.shift();
        const res = await uploadPhotoWithRetry(f, 3);
        if (res.ok) { ok++; }
        else {
          newFailed.push({
            id: `fail_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            file: f,
            name: f.name || "photo",
            previewUrl: (f.type || "").startsWith("image/") ? URL.createObjectURL(f) : "",
            attempts: res.attempts,
            lastError: res.error,
          });
        }
      }
    }));
    if (tid) toast.dismiss(tid);
    if (newFailed.length) {
      setFailedUploads(prev => [...prev, ...newFailed]);
      toast.error(`${newFailed.length} of ${arr.length} photo${arr.length === 1 ? "" : "s"} failed — tap the red badge to retry`, { duration: 6000 });
    } else {
      toast.success(arr.length === 1 ? "Photo added" : `${ok} photos added`);
    }
    await onReload();
    setUploading(false);
  };

  const retryFailed = async () => {
    if (failedUploads.length === 0) return;
    setUploading(true);
    const stillFailed = [];
    const toRetry = [...failedUploads];
    setFailedUploads([]);  // clear + repopulate any that still fail
    const tid = toast.loading(`Retrying ${toRetry.length} photo${toRetry.length === 1 ? "" : "s"}…`);
    for (const item of toRetry) {
      const res = await uploadPhotoWithRetry(item.file, 3);
      if (!res.ok) stillFailed.push({ ...item, attempts: item.attempts + res.attempts, lastError: res.error });
    }
    toast.dismiss(tid);
    if (stillFailed.length) {
      setFailedUploads(stillFailed);
      toast.error(`${stillFailed.length} still failing. Check your signal and try again.`);
    } else {
      toast.success("All retried photos uploaded!");
    }
    await onReload();
    setUploading(false);
  };

  const discardFailed = (id) => {
    setFailedUploads(prev => prev.filter(x => x.id !== id));
  };

  const saveDraft = () => onSave({ condition, notes, sub_checks: subChecks }, { toastMsg: "Saved" });
  const complete = () => onSave({ condition, notes, sub_checks: subChecks, completed: true }, { toastMsg: `${room.room} complete`, advance: true });
  const reopen = () => onSave({ completed: false }, { toastMsg: "Reopened" });

  const isKitchenRoom = /kitchen/i.test(room.room);

  return (
    <div className="space-y-5" data-testid={`insp-room-panel-${room.room}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <House weight="bold" size={22} className="text-[#0f172a]"/>
            <h3 className="font-display font-black text-2xl uppercase tracking-tight truncate">{room.room}</h3>
          </div>
          {room.completed && (
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider">
              <CheckCircle weight="fill" size={12}/> Complete — {room.completed_by_name || "—"}
            </div>
          )}
          {!room.completed && room.started_at && (
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-black uppercase tracking-wider">
              <Play weight="fill" size={12}/> In Progress
            </div>
          )}
        </div>
        {isKitchenRoom && onLogAppliance && (
          <button onClick={onLogAppliance} data-testid="insp-room-log-appliance-btn"
            className="h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-wider bg-amber-50 hover:bg-amber-100 inline-flex items-center gap-1 whitespace-nowrap">
            <Wrench weight="bold" size={14}/> Log Appliance
          </button>
        )}
      </div>

      <div>
        <div className="text-xs font-black uppercase tracking-wider text-slate-600">Condition</div>
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
          {CONDITIONS.map(c => {
            const on = condition === c.key;
            return (
              <button key={c.key} onClick={() => setCondition(c.key)}
                data-testid={`insp-cond-${c.key}`}
                className={`h-14 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider transition-all ${
                  on ? `${c.color} text-white shadow-[3px_3px_0px_#0f172a]` : "bg-white hover:bg-slate-50"
                }`}>
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {!isNA && (
        <div>
          <div className="flex items-center justify-between">
            <div className="text-xs font-black uppercase tracking-wider text-slate-600">
              Photos {hasPhoto ? `· ${(room.photos || []).length}` : "· Required"}
            </div>
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(room.photos || []).map((p, i) => {
              const isVideo = p.media_type === "video" || (p.content_type || "").startsWith("video/");
              const flagged = !!p.chargeable_to_tenant;
              return (
                <div key={i} className={`relative aspect-square border-2 ${flagged ? "border-red-600 ring-2 ring-red-600/40" : "border-[#0f172a]"} bg-slate-100 overflow-hidden group`}
                  data-testid={`insp-photo-${i}`}>
                  <button type="button" onClick={() => onOpenLightbox && onOpenLightbox({ path: p.path, isVideo })}
                    data-testid={`insp-photo-open-${i}`}
                    className="absolute inset-0 block w-full h-full">
                    {isVideo ? (
                      <video src={fileUrl(p.path)} className="w-full h-full object-cover" muted/>
                    ) : (
                      <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover"/>
                    )}
                  </button>
                  {p.annotated && (
                    <span className="absolute top-1 right-1 px-1.5 py-0.5 text-[9px] font-black uppercase bg-cyan-500 text-white tracking-wider">Marked</span>
                  )}
                  {flagged && (
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-black uppercase bg-red-600 text-white tracking-wider">Tenant $</span>
                  )}
                  {!isVideo && (
                    <div className="absolute inset-x-0 bottom-0 flex gap-1 p-1 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity bg-gradient-to-t from-black/70 to-transparent">
                      <button onClick={(e) => { e.stopPropagation(); onAnnotate(p); }}
                        data-testid={`insp-annotate-${i}`}
                        className="flex-1 h-8 bg-white/95 hover:bg-white text-[#0f172a] font-black uppercase text-[10px] tracking-wider inline-flex items-center justify-center gap-1 border border-[#0f172a]">
                        <PaintBrush weight="bold" size={12}/> Mark
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onFlag(p, !flagged); }}
                        data-testid={`insp-flag-${i}`}
                        className={`flex-1 h-8 font-black uppercase text-[10px] tracking-wider inline-flex items-center justify-center gap-1 border ${flagged ? "bg-red-600 border-red-600 text-white" : "bg-white/95 border-[#0f172a] text-[#0f172a] hover:bg-red-50"}`}>
                        <CurrencyDollar weight="bold" size={12}/> {flagged ? "Flagged" : "Flag"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <button onClick={() => cameraRef.current?.click()} disabled={uploading}
              data-testid="insp-camera-btn"
              className="aspect-square border-2 border-[#00C4CC] bg-cyan-50 hover:bg-cyan-100 flex flex-col items-center justify-center text-[#0f172a] font-black uppercase text-xs tracking-wider">
              <Camera weight="fill" size={26} className="text-[#00C4CC]"/>
              <span className="mt-1">{uploading ? "…" : "Camera"}</span>
            </button>
            <button onClick={() => galleryRef.current?.click()} disabled={uploading}
              data-testid="insp-gallery-btn"
              className="aspect-square dashed-drop flex flex-col items-center justify-center text-[#0f172a] font-black uppercase text-xs tracking-wider hover:bg-slate-50">
              <ClipboardText weight="bold" size={26}/>
              <span className="mt-1">{uploading ? "…" : "Gallery"}</span>
            </button>
          </div>
          {/* iOS Safari intermittently ignores clicks on file inputs with display:none.
              Use sr-only positioning so the input is still in the layout tree. */}
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only"
            onChange={(e) => { if (e.target.files?.length) { uploadPhotos(e.target.files); e.target.value = ""; }}}/>
          <input ref={galleryRef} type="file" accept="image/*,video/*" multiple className="sr-only"
            onChange={(e) => { if (e.target.files?.length) { uploadPhotos(e.target.files); e.target.value = ""; }}}/>
          <p className="mt-1 text-[11px] text-slate-500">
            <b>Camera</b> shoots a fresh photo · <b>Gallery</b> lets you pick photos already on the device · Tap <b>Mark</b> to circle damage · Tap <b>$ Flag</b> to charge back.
          </p>
          {failedUploads.length > 0 && (
            <div className="mt-3 p-3 border-2 border-red-500 bg-red-50" data-testid="failed-uploads-panel">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-black uppercase tracking-widest text-[11px] text-red-700 inline-flex items-center gap-1.5">
                  <Warning weight="fill" size={14}/> {failedUploads.length} upload{failedUploads.length === 1 ? "" : "s"} failed
                </div>
                <button onClick={retryFailed} disabled={uploading} data-testid="retry-failed-uploads-btn"
                  className="tactile-btn h-8 px-3 bg-red-600 text-white font-black uppercase tracking-widest text-[11px] disabled:opacity-50 inline-flex items-center gap-1">
                  <ArrowUUpLeft size={12}/> Retry All
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {failedUploads.map(f => (
                  <div key={f.id} className="relative flex flex-col items-center bg-white border-2 border-red-500 p-1 w-16" title={f.lastError}>
                    {f.previewUrl ? (
                      <img src={f.previewUrl} alt="" className="h-12 w-14 object-cover"/>
                    ) : (
                      <div className="h-12 w-14 bg-slate-200 flex items-center justify-center text-[9px] font-black uppercase">FILE</div>
                    )}
                    <div className="text-[9px] font-bold text-red-600 mt-0.5 truncate w-full text-center">{f.name.slice(-10)}</div>
                    <button onClick={() => discardFailed(f.id)} type="button"
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-600 text-white text-xs font-black flex items-center justify-center">×</button>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[10px] text-red-800">
                Uploads keep failing? Check your cell signal, then hit Retry All. Photos won't be counted in the room total until they upload successfully.
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs font-black uppercase tracking-wider text-slate-600">
            Notes {notesRequired && <span className="text-red-600">· Required</span>}
          </div>
          <VoiceRecorder
            disabled={room.completed}
            onTranscript={(txt) => setNotes(prev => prev ? `${prev} ${txt}` : txt)}
          />
        </div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} data-testid="insp-notes-input"
          placeholder={notesRequired ? "Describe the damage / issue…" : "Tap the mic to talk or type your observations…"}
          className={`mt-2 w-full min-h-[90px] border-2 p-2 text-sm ${notesRequired && (notes || "").trim().length < 3 ? "border-red-600" : "border-[#0f172a]"}`}/>
      </div>

      {!isNA && (
        <SubChecksSection subChecks={subChecks} setSubChecks={setSubChecks} completed={room.completed}/>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t-2 border-dashed border-slate-300">
        {!room.completed && (
          <>
            <button onClick={saveDraft} disabled={saving} data-testid="insp-save-draft-btn"
              className="h-11 px-4 border-2 border-[#0f172a] font-black uppercase text-sm tracking-wider bg-white hover:bg-slate-50">
              Save Draft
            </button>
            <button onClick={complete} disabled={saving || !canComplete}
              data-testid="insp-complete-btn"
              title={missingReason || ""}
              className="tactile-btn h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              <CheckCircle weight="fill" size={16}/> {isNA ? "Mark N/A & Next" : "Complete Room"}
            </button>
          </>
        )}
        {room.completed && (
          <button onClick={reopen} disabled={saving} data-testid="insp-reopen-btn"
            className="h-11 px-4 border-2 border-amber-500 text-amber-700 font-black uppercase text-sm tracking-wider bg-white hover:bg-amber-50">
            Reopen Room
          </button>
        )}
        {!canComplete && !room.completed && missingReason && (
          <div className="w-full text-xs font-bold text-red-600 flex items-center gap-1">
            <Warning weight="fill" size={14}/> {missingReason}
          </div>
        )}
      </div>
    </div>
  );
}

function PropertyPanel({ jobId, address, propertyCfg, recommendations, onReload, onJobReload, onOpenMeter, job, phase }) {
  const m = (propertyCfg && propertyCfg.maintenance) || {};
  const [f, setF] = useState({
    filter_size: m.filter_size || "",
    filter_type: m.filter_type || "",
    hvac_last_serviced: m.hvac_last_serviced || "",
    maintenance_notes: m.maintenance_notes || "",
  });
  const [savingM, setSavingM] = useState(false);
  const [showRecoForm, setShowRecoForm] = useState(false);
  const [keys, setKeys] = useState({
    returned: !!job?.keys_returned,
    count: job?.keys_returned_count ?? "",
    notes: job?.keys_returned_notes || "",
  });
  const [savingKeys, setSavingKeys] = useState(false);

  useEffect(() => {
    setKeys({
      returned: !!job?.keys_returned,
      count: job?.keys_returned_count ?? "",
      notes: job?.keys_returned_notes || "",
    });
  }, [job?.keys_returned, job?.keys_returned_count, job?.keys_returned_notes]);

  const saveKeys = async () => {
    setSavingKeys(true);
    try {
      await api.post(`/jobs/${jobId}/keys-returned`, {
        returned: !!keys.returned,
        count: Number(keys.count) || 0,
        notes: keys.notes || "",
      });
      toast.success(keys.returned ? "Keys marked returned" : "Keys status updated");
      await onReload();
      if (onJobReload) await onJobReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSavingKeys(false); }
  };

  useEffect(() => {
    const cur = (propertyCfg && propertyCfg.maintenance) || {};
    setF({
      filter_size: cur.filter_size || "",
      filter_type: cur.filter_type || "",
      hvac_last_serviced: cur.hvac_last_serviced || "",
      maintenance_notes: cur.maintenance_notes || "",
    });
  }, [propertyCfg]);

  const saveMaintenance = async () => {
    if (!address) { toast.error("No property address"); return; }
    setSavingM(true);
    try {      await api.put("/property-maintenance", { address, ...f });
      toast.success("Property maintenance info saved");
      await onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSavingM(false); }
  };

  const deleteReco = async (id) => {
    try {
      await api.delete(`/jobs/${jobId}/service-recommendation/${id}`);
      toast.success("Recommendation removed");
      await onReload();
      if (onJobReload) await onJobReload();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const addReco = async (payload) => {
    try {
      await api.post(`/jobs/${jobId}/service-recommendation`, payload);
      toast.success("Recommendation added");
      setShowRecoForm(false);
      await onReload();
      if (onJobReload) await onJobReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add");
    }
  };

  const toggleReco = async (id, nextResolved) => {
    try {
      await api.patch(`/jobs/${jobId}/service-recommendation/${id}?resolved=${nextResolved}`);
      toast.success(nextResolved ? "Marked resolved" : "Reopened");
      await onReload();
      if (onJobReload) await onJobReload();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const deleteMeter = async (meterId) => {
    if (!confirm("Delete this meter record?")) return;
    try {
      await api.delete(`/property-meters/${meterId}?address=${encodeURIComponent(address)}`);
      toast.success("Meter removed");
      await onReload();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const meters = (propertyCfg && propertyCfg.meters) || [];
  const METER_STYLES = {
    electric: { bg: "bg-amber-50", border: "border-amber-500", pill: "bg-amber-500", label: "ELECTRIC" },
    gas: { bg: "bg-orange-50", border: "border-orange-500", pill: "bg-orange-500", label: "GAS" },
    water: { bg: "bg-sky-50", border: "border-sky-500", pill: "bg-sky-500", label: "WATER" },
    sewer: { bg: "bg-stone-50", border: "border-stone-500", pill: "bg-stone-500", label: "SEWER" },
    other: { bg: "bg-slate-50", border: "border-slate-400", pill: "bg-slate-500", label: "OTHER" },
  };

  const PRIORITY_STYLES = {
    low: "bg-slate-100 text-slate-700 border-slate-300",
    normal: "bg-amber-50 text-amber-800 border-amber-300",
    high: "bg-red-50 text-red-800 border-red-400",
  };

  return (
    <div className="space-y-6" data-testid="insp-property-panel">
      <div>
        <div className="flex items-center gap-2">
          <Wind weight="bold" size={22} className="text-amber-600"/>
          <h3 className="font-display font-black text-2xl uppercase tracking-tight">Property Info</h3>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">
          Saves to <b>{address || "this property"}</b> — every future turn/repair job will already have it.
        </p>
      </div>

      <div className="p-4 border-2 border-[#0f172a] bg-slate-50 space-y-3">
        <div className="text-[11px] font-black uppercase tracking-widest text-slate-600">HVAC Filter</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Filter Size</span>
            <input value={f.filter_size} onChange={e => setF({ ...f, filter_size: e.target.value })}
              data-testid="prop-filter-size" placeholder="16 x 20 x 1"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Filter Type</span>
            <input value={f.filter_type} onChange={e => setF({ ...f, filter_type: e.target.value })}
              data-testid="prop-filter-type" placeholder="MERV 8 pleated"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
          </label>
        </div>
        <label className="block">
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">HVAC Last Serviced</span>
          <input type="date" value={f.hvac_last_serviced} onChange={e => setF({ ...f, hvac_last_serviced: e.target.value })}
            data-testid="prop-hvac-serviced"
            className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
        </label>
        <label className="block">
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">General Maintenance Notes</span>
          <textarea value={f.maintenance_notes} onChange={e => setF({ ...f, maintenance_notes: e.target.value })}
            data-testid="prop-maintenance-notes" rows={2}
            placeholder="Water heater is 12 yrs old, GFCI in garage tripping monthly…"
            className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] resize-none text-sm"/>
        </label>
        <button onClick={saveMaintenance} disabled={savingM} data-testid="prop-maintenance-save-btn"
          className="tactile-btn h-11 px-4 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
          <FloppyDisk weight="fill" size={14}/> {savingM ? "Saving…" : "Save Property Info"}
        </button>
        {m.updated_at && (
          <div className="text-[10px] text-slate-500 font-medium">
            Last updated {new Date(m.updated_at).toLocaleDateString()} by {m.updated_by_name || "—"}
          </div>
        )}
      </div>

      {/* Keys Returned — Move-Out close-out */}
      {phase === "move_out" && (
        <div className="p-4 border-2 border-[#0f172a] bg-cyan-50" data-testid="insp-keys-returned-block">
          <div className="flex items-center gap-2">
            <Key weight="fill" size={20} className="text-[#00C4CC]"/>
            <h4 className="font-display font-black text-base uppercase tracking-wider">Keys Returned</h4>
            {job?.keys_returned && (
              <span className="ml-auto px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white rounded-sm">
                ✓ Received
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-slate-600 font-medium">
            Tenant offboarding — confirm every physical key, fob, garage remote, or mailbox key is back before you close this sweep.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!keys.returned}
                onChange={e => setKeys(prev => ({ ...prev, returned: e.target.checked }))}
                data-testid="keys-returned-checkbox"
                className="h-5 w-5 accent-[#00C4CC]"/>
              <span className="font-black uppercase text-xs tracking-wider">All keys returned</span>
            </label>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <label className="col-span-1 block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Key Count</span>
              <input type="number" min="0" max="20" value={keys.count}
                onChange={e => setKeys(prev => ({ ...prev, count: e.target.value === "" ? "" : Number(e.target.value) }))}
                data-testid="keys-returned-count"
                placeholder="0"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
            </label>
            <label className="col-span-2 block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Notes (missing keys, replacements owed, etc.)</span>
              <input value={keys.notes}
                onChange={e => setKeys(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="keys-returned-notes"
                placeholder="2 door keys, 1 mailbox, 1 fob — garage remote missing (charge $75)"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </label>
          </div>
          <button onClick={saveKeys} disabled={savingKeys} data-testid="keys-returned-save-btn"
            className="mt-3 tactile-btn h-11 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
            <Key weight="fill" size={14}/> {savingKeys ? "Saving…" : "Save Keys Status"}
          </button>
          {job?.keys_returned_at && (
            <div className="mt-2 text-[10px] text-slate-500 font-medium">
              Received {new Date(job.keys_returned_at).toLocaleString()} by {job.keys_returned_by_name || "—"}
            </div>
          )}
        </div>
      )}

      <div className="p-4 border-2 border-[#0f172a] bg-white">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClipboardText weight="bold" size={18} className="text-red-600"/>
            <h4 className="font-display font-black text-base uppercase tracking-wider">
              Service Recommendations · {recommendations.length}
            </h4>
          </div>
          <button onClick={() => setShowRecoForm(true)} data-testid="reco-add-btn"
            className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-wider bg-white hover:bg-slate-50 inline-flex items-center gap-1">
            <Plus weight="bold" size={14}/> Add
          </button>
        </div>
        <div className="mt-1 text-[11px] text-slate-500 font-medium">
          Preventable maintenance the office should schedule (e.g. "Reglaze tub in 6mo", "Recaulk shower").
        </div>

        {showRecoForm && (
          <RecoForm onSubmit={addReco} onCancel={() => setShowRecoForm(false)}/>
        )}

        {recommendations.length === 0 && !showRecoForm && (
          <div className="mt-3 p-6 text-center border-2 border-dashed border-slate-300 text-slate-400 text-sm font-medium">
            No recommendations yet. Click <b>Add</b> to log a preventable maintenance item.
          </div>
        )}

        <ul className="mt-3 space-y-2">
          {recommendations.map(r => {
            const resolved = !!r.resolved;
            return (
              <li key={r.id} data-testid={`reco-item-${r.id}`}
                className={`p-3 border-2 ${resolved ? "border-emerald-500 bg-emerald-50/50" : (PRIORITY_STYLES[r.priority] || PRIORITY_STYLES.normal)} rounded-sm flex items-start gap-3`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.category && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase bg-[#0f172a] text-white tracking-wider">
                        {r.category}
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${
                      r.priority === "high" ? "bg-red-600 text-white" :
                      r.priority === "low" ? "bg-slate-500 text-white" : "bg-amber-500 text-white"
                    }`}>{r.priority}</span>
                    {resolved && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase bg-emerald-600 text-white tracking-wider">
                        ✓ Resolved
                      </span>
                    )}
                    <span className={`font-black text-sm ${resolved ? "line-through text-slate-500" : ""}`}>{r.title}</span>
                  </div>
                  {r.notes && <p className={`mt-1 text-xs whitespace-pre-wrap ${resolved ? "text-slate-500 line-through" : "text-slate-700"}`}>{r.notes}</p>}
                  <div className="mt-1 text-[10px] font-medium text-slate-500">
                    Added by {r.created_by_name || "—"} · {new Date(r.created_at).toLocaleDateString()}
                    {resolved && r.resolved_at && (
                      <> · <span className="text-emerald-700 font-bold">Resolved {new Date(r.resolved_at).toLocaleDateString()} by {r.resolved_by_name || "—"}</span></>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => toggleReco(r.id, !resolved)} data-testid={`reco-toggle-${r.id}`}
                    title={resolved ? "Reopen" : "Mark resolved"}
                    className={`h-8 w-8 border-2 ${resolved ? "border-amber-500 text-amber-600 hover:bg-amber-500" : "border-emerald-500 text-emerald-600 hover:bg-emerald-500"} hover:text-white flex items-center justify-center`}>
                    <CheckCircle weight={resolved ? "regular" : "fill"} size={14}/>
                  </button>
                  <button onClick={() => deleteReco(r.id)} data-testid={`reco-delete-${r.id}`}
                    className="h-8 w-8 border-2 border-red-400 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center">
                    <Trash weight="bold" size={14}/>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="p-4 border-2 border-[#0f172a] bg-white" data-testid="prop-meters-section">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Fire weight="bold" size={18} className="text-orange-600"/>
            <h4 className="font-display font-black text-base uppercase tracking-wider">
              Utility Meters · {meters.length}
            </h4>
          </div>
          <button onClick={onOpenMeter} data-testid="prop-add-meter-btn"
            className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-wider bg-white hover:bg-slate-50 inline-flex items-center gap-1">
            <Plus weight="bold" size={14}/> Log Meter
          </button>
        </div>
        <div className="mt-1 text-[11px] text-slate-500 font-medium">
          Gas · electric · water. AI scans the meter face and grabs provider, number, and current reading.
        </div>
        {meters.length === 0 && (
          <div className="mt-3 p-6 text-center border-2 border-dashed border-slate-300 text-slate-400 text-sm font-medium">
            No meters logged. Click <b>Log Meter</b> to scan the first one.
          </div>
        )}
        <ul className="mt-3 space-y-2">
          {meters.map(m => {
            const st = METER_STYLES[m.utility_type] || METER_STYLES.other;
            return (
              <li key={m.id} data-testid={`meter-item-${m.id}`}
                className={`p-3 border-2 ${st.border} ${st.bg} rounded-sm flex items-start gap-3`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase text-white tracking-wider ${st.pill}`}>{st.label}</span>
                    {m.provider && <span className="font-black text-sm">{m.provider}</span>}
                    {m.meter_number && <span className="text-xs font-mono font-bold text-slate-700">#{m.meter_number}</span>}
                  </div>
                  <div className="mt-1 text-xs text-slate-700 grid grid-cols-2 gap-x-3">
                    {m.reading && <div><b>Reading:</b> <span className="font-mono">{m.reading}</span></div>}
                    {m.service_id && <div><b>Svc ID:</b> <span className="font-mono">{m.service_id}</span></div>}
                    {m.location && <div><b>Location:</b> {m.location}</div>}
                    {m.reading_at && <div><b>Date:</b> {m.reading_at.slice(0,10)}</div>}
                  </div>
                  {m.notes && <p className="mt-1 text-xs text-slate-600">{m.notes}</p>}
                  <div className="mt-1 text-[10px] text-slate-500 font-medium">
                    Logged by {m.created_by_name || "—"} · {new Date(m.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button onClick={() => deleteMeter(m.id)} data-testid={`meter-delete-${m.id}`}
                  className="h-8 w-8 border-2 border-red-400 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center">
                  <Trash weight="bold" size={14}/>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

const UTILITY_OPTIONS = [
  { key: "electric", label: "Electric" },
  { key: "gas", label: "Gas" },
  { key: "water", label: "Water" },
  { key: "sewer", label: "Sewer" },
  { key: "other", label: "Other" },
];

function PaintPanel({ jobId, rooms, paintRec, onSaved }) {
  const [f, setF] = useState({
    ceiling_color: (paintRec?.ceiling_color) || "Behr Ceiling White",
    trim_color: (paintRec?.trim_color) || "Behr Ultra Pure White",
    wall_color: (paintRec?.wall_color) || "Repose Grey (SW 7015)",
    rooms_to_paint: paintRec?.rooms_to_paint || [],
    notes: paintRec?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setF({
      ceiling_color: (paintRec?.ceiling_color) || "Behr Ceiling White",
      trim_color: (paintRec?.trim_color) || "Behr Ultra Pure White",
      wall_color: (paintRec?.wall_color) || "Repose Grey (SW 7015)",
      rooms_to_paint: paintRec?.rooms_to_paint || [],
      notes: paintRec?.notes || "",
    });
  }, [paintRec]);

  const toggleRoom = (r) => {
    setF(prev => ({
      ...prev,
      rooms_to_paint: prev.rooms_to_paint.includes(r)
        ? prev.rooms_to_paint.filter(x => x !== r)
        : [...prev.rooms_to_paint, r],
    }));
  };

  const allSelected = rooms.length > 0 && f.rooms_to_paint.length === rooms.length;
  const toggleAll = () => {
    setF(prev => ({ ...prev, rooms_to_paint: allSelected ? [] : rooms.map(r => r.room) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}/paint-recommendation`, f);
      toast.success("Paint recommendation saved");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="insp-paint-panel">
      <div>
        <div className="flex items-center gap-2">
          <PaintBrush weight="bold" size={22} className="text-violet-600"/>
          <h3 className="font-display font-black text-2xl uppercase tracking-tight">Inspector Recommendation · Paint</h3>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">
          Colors & rooms the inspector recommends for the turn crew. Included on the Turn Report PDF.
        </p>
      </div>

      <div className="p-4 border-2 border-[#0f172a] bg-white space-y-3">
        <div className="text-[11px] font-black uppercase tracking-widest text-slate-600">Recommended Colors</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Ceilings</span>
            <input value={f.ceiling_color} onChange={e => setF({ ...f, ceiling_color: e.target.value })}
              data-testid="paint-ceiling-color" placeholder="Behr Ceiling White"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Trim</span>
            <input value={f.trim_color} onChange={e => setF({ ...f, trim_color: e.target.value })}
              data-testid="paint-trim-color" placeholder="Behr Ultra Pure White (semi-gloss)"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Walls / Body</span>
            <input value={f.wall_color} onChange={e => setF({ ...f, wall_color: e.target.value })}
              data-testid="paint-wall-color" placeholder="Repose Grey (SW 7015)"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
          </label>
        </div>
        <p className="text-[10px] text-slate-500 font-medium italic">
          Defaults reflect the tenant standard: Behr Ceiling White · Repose Grey walls.
        </p>
      </div>

      <div className="p-4 border-2 border-[#0f172a] bg-white space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-600">
            Rooms to Paint · {f.rooms_to_paint.length}/{rooms.length}
          </div>
          {rooms.length > 0 && (
            <button type="button" onClick={toggleAll} data-testid="paint-toggle-all"
              className="h-8 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest bg-white hover:bg-slate-50">
              {allSelected ? "Clear all" : "Paint all rooms"}
            </button>
          )}
        </div>
        {rooms.length === 0 ? (
          <div className="text-xs text-slate-500 font-medium italic">No rooms configured on this job.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {rooms.map(r => {
              const on = f.rooms_to_paint.includes(r.room);
              return (
                <button key={r.room} type="button" onClick={() => toggleRoom(r.room)}
                  data-testid={`paint-room-${r.room}`}
                  className={`h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-wider text-left transition-all ${
                    on ? "bg-violet-600 text-white shadow-[3px_3px_0px_#0f172a]" : "bg-white hover:bg-violet-50"
                  }`}>
                  {on ? "✓ " : ""}{r.room}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="p-4 border-2 border-[#0f172a] bg-white space-y-3">
        <label className="block">
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Additional Notes</span>
          <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}
            data-testid="paint-notes" rows={3}
            placeholder="Kitchen ceiling has water stain — prime before repaint. Kids' bedroom needs blocking primer over marker."
            className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] resize-none text-sm"/>
        </label>
      </div>

      <button onClick={save} disabled={saving} data-testid="paint-save-btn"
        className="tactile-btn w-full sm:w-auto h-11 px-6 bg-violet-600 hover:bg-violet-700 text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
        <FloppyDisk weight="fill" size={16}/> {saving ? "Saving…" : "Save Paint Recommendation"}
      </button>
      {paintRec?.updated_at && (
        <div className="text-[10px] text-slate-500 font-medium">
          Last updated {new Date(paintRec.updated_at).toLocaleDateString()} by {paintRec.updated_by_name || "—"}
        </div>
      )}
    </div>
  );
}

function MeterLogModal({ address, onClose, onSaved }) {
  const [f, setF] = useState({
    utility_type: "electric", provider: "", meter_number: "", service_id: "",
    reading: "", location: "", notes: "", photo_path: "",
  });
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const scanRef = useRef();

  const scan = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/property-meters/scan", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const p = r.data || {};
      setF(prev => ({
        ...prev,
        utility_type: p.utility_type || prev.utility_type,
        provider: p.provider || prev.provider,
        meter_number: p.meter_number || prev.meter_number,
        service_id: p.service_id || prev.service_id,
        reading: p.reading || prev.reading,
        notes: p.notes ? (prev.notes ? `${prev.notes} · ${p.notes}` : p.notes) : prev.notes,
        photo_path: p.photo_path || "",
      }));
      const gotAny = !!(p.provider || p.meter_number || p.reading);
      toast[gotAny ? "success" : "info"](gotAny ? "Meter parsed — review below" : "AI couldn't read it — enter manually");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.provider && !f.meter_number && !f.reading) {
      toast.error("Enter at least a provider, meter #, or reading");
      return;
    }
    setBusy(true);
    try {
      await api.post("/property-meters", { address, ...f });
      toast.success("Meter saved to property");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-5 my-8"
        data-testid="meter-log-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl uppercase">Log Utility Meter</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">
          Saves to <b>{address}</b> so every future visit has the meter #, provider, and last reading.
        </p>

        <button type="button" onClick={() => scanRef.current?.click()} disabled={scanning || busy}
          data-testid="meter-scan-btn"
          className="mt-3 tactile-btn w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <Camera weight="fill" size={16}/> {scanning ? "AI reading meter…" : "Scan Meter with AI"}
        </button>
        <input ref={scanRef} type="file" accept="image/*" capture="environment" className="sr-only"
          onChange={(e) => { if (e.target.files?.[0]) { scan(e.target.files[0]); e.target.value = ""; }}}/>
        <p className="mt-1 text-[10px] text-slate-500 text-center font-medium">
          Snap or pick a photo · Claude reads provider · meter # · reading
        </p>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Utility Type</span>
            <select value={f.utility_type} onChange={upd("utility_type")} data-testid="meter-type"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] bg-white">
              {UTILITY_OPTIONS.map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Provider</span>
              <input value={f.provider} onChange={upd("provider")} data-testid="meter-provider"
                placeholder="PECO / PGW" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </label>
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Location</span>
              <input value={f.location} onChange={upd("location")} data-testid="meter-location"
                placeholder="Basement / Side yard" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Meter #</span>
            <input value={f.meter_number} onChange={upd("meter_number")} data-testid="meter-number"
              placeholder="12345678" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Reading</span>
            <input value={f.reading} onChange={upd("reading")} data-testid="meter-reading"
              placeholder="48273 kWh" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Service ID (optional)</span>
            <input value={f.service_id} onChange={upd("service_id")} data-testid="meter-service-id"
              placeholder="Account or point-of-delivery" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Notes</span>
            <textarea value={f.notes} onChange={upd("notes")} data-testid="meter-notes" rows={2}
              placeholder="Utility rooms locked, digital display, etc."
              className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] resize-none text-sm"/>
          </label>
        </div>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || scanning} data-testid="meter-submit"
            className="tactile-btn flex-1 h-11 bg-orange-500 text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-1 disabled:opacity-60">
            <FloppyDisk weight="fill" size={14}/> {busy ? "Saving…" : "Save Meter"}
          </button>
        </div>
      </form>
    </div>
  );
}

const RECO_CATEGORIES = ["HVAC", "Plumbing", "Electrical", "Appliance", "Roof / Exterior", "Interior", "Safety", "Other"];

function RecoForm({ onSubmit, onCancel }) {
  const [f, setF] = useState({ category: "HVAC", title: "", notes: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    await onSubmit(f);
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-3 p-3 border-2 border-amber-500 bg-amber-50 space-y-3" data-testid="reco-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Category</span>
          <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })}
            data-testid="reco-category"
            className="mt-1 w-full h-10 px-2 border-2 border-[#0f172a] bg-white text-sm">
            {RECO_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Priority</span>
          <select value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })}
            data-testid="reco-priority"
            className="mt-1 w-full h-10 px-2 border-2 border-[#0f172a] bg-white text-sm">
            <option value="low">Low · someday</option>
            <option value="normal">Normal · next turn</option>
            <option value="high">High · schedule now</option>
          </select>
        </label>
      </div>
      <label className="block">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Title</span>
        <input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="reco-title"
          placeholder="Recaulk shower · Replace water heater in 2 yrs"
          className="mt-1 w-full h-10 px-3 border-2 border-[#0f172a] text-sm"/>
      </label>
      <label className="block">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Notes (optional)</span>
        <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="reco-notes" rows={2}
          placeholder="What did you see, why is it worth doing…"
          className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] resize-none text-sm"/>
      </label>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel}
          className="flex-1 h-10 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white">
          Cancel
        </button>
        <button type="submit" disabled={busy} data-testid="reco-submit"
          className="tactile-btn flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-wider disabled:opacity-60">
          {busy ? "Adding…" : "Add Recommendation"}
        </button>
      </div>
    </form>
  );
}

const APPLIANCE_TYPES = [
  "Refrigerator", "Range / Stove", "Oven", "Cooktop", "Microwave", "Dishwasher",
  "Washer", "Dryer", "Water Heater", "Furnace", "AC Condenser", "HVAC Air Handler",
  "Garbage Disposal", "Range Hood", "Ceiling Fan", "Smoke Detector", "Other",
];

function ApplianceLogModal({ jobId, defaultLocation, onClose, onSaved }) {
  const [f, setF] = useState({
    type: "", brand: "", model: "", serial: "",
    location: defaultLocation || "Kitchen", notes: "", save_to_property: true,
  });
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanRef = useRef();

  const scan = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/property-assets/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const p = r.data || {};
      setF(prev => ({
        ...prev,
        type: p.type || prev.type,
        brand: p.make || prev.brand,
        model: p.model || prev.model,
        serial: p.serial || prev.serial,
        notes: p.notes ? (prev.notes ? `${prev.notes} · ${p.notes}` : p.notes) : prev.notes,
      }));
      const gotAny = !!(p.make || p.model || p.serial || p.type);
      toast[gotAny ? "success" : "info"](gotAny ? "Data-plate parsed — review below" : "AI couldn't read the plate — enter manually");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.type && !f.brand && !f.model && !f.serial) {
      toast.error("Enter at least a type, brand, model, or serial");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/appliance`, f);
      toast.success(f.save_to_property ? "Appliance logged & saved to property" : "Appliance logged");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-5 my-8"
        data-testid="appliance-log-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl uppercase">Log Appliance</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">
          Serial # and model save to this property so future turns/repairs already have it.
        </p>

        <button type="button" onClick={() => scanRef.current?.click()} disabled={scanning || busy}
          data-testid="appliance-scan-btn"
          className="mt-3 tactile-btn w-full h-12 bg-gradient-to-r from-violet-600 to-[#00C4CC] text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <Camera weight="fill" size={16}/> {scanning ? "AI reading nameplate…" : "Scan Data-Plate with AI"}
        </button>
        <input ref={scanRef} type="file" accept="image/*" capture="environment" className="sr-only"
          onChange={(e) => { if (e.target.files?.[0]) { scan(e.target.files[0]); e.target.value = ""; }}}/>
        <p className="mt-1 text-[10px] text-slate-500 text-center font-medium">
          Take a photo OR pick from gallery · Claude reads brand · model · serial · specs
        </p>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Type</span>
            <select value={f.type} onChange={upd("type")} data-testid="appliance-type"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] bg-white">
              <option value="">— Pick a type —</option>
              {APPLIANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              {f.type && !APPLIANCE_TYPES.includes(f.type) && <option value={f.type}>{f.type} (AI)</option>}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Brand</span>
              <input value={f.brand} onChange={upd("brand")} data-testid="appliance-brand"
                placeholder="Whirlpool" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </label>
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Location</span>
              <input value={f.location} onChange={upd("location")} data-testid="appliance-location"
                placeholder="Kitchen" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Model #</span>
            <input value={f.model} onChange={upd("model")} data-testid="appliance-model"
              placeholder="WRF535SWHZ" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Serial #</span>
            <input value={f.serial} onChange={upd("serial")} data-testid="appliance-serial"
              placeholder="K91234567" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono"/>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Notes</span>
            <textarea value={f.notes} onChange={upd("notes")} data-testid="appliance-notes" rows={2}
              placeholder="Missing filter, dent on door, purchased 2020…"
              className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] resize-none text-sm"/>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.save_to_property} data-testid="appliance-save-property"
              onChange={e => setF({ ...f, save_to_property: e.target.checked })}
              className="h-4 w-4 accent-[#0f172a]"/>
            <span className="text-xs font-bold text-slate-700">Also save to the property's asset registry</span>
          </label>
        </div>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || scanning} data-testid="appliance-submit"
            className="tactile-btn flex-1 h-11 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-1 disabled:opacity-60">
            <FloppyDisk weight="fill" size={14}/> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Turn Ballpark chip ────────────────────────────────────────────────
// Live cost estimate derived from room conditions. Updates every save.
function TurnBallparkChip({ rooms }) {
  const [open, setOpen] = useState(false);
  const bp = computeTurnBallpark(rooms);
  if (bp.rated === 0) return null;
  const tone = bp.total < 500 ? "bg-emerald-500" :
    bp.total < 1500 ? "bg-lime-500" :
    bp.total < 3500 ? "bg-amber-500" :
    bp.total < 7500 ? "bg-orange-500" : "bg-red-600";
  const confTone = bp.confidence === "high" ? "text-emerald-200" :
    bp.confidence === "medium" ? "text-amber-200" : "text-orange-200";
  return (
    <div className="relative inline-flex">
      <button type="button" onClick={() => setOpen(o => !o)}
        data-testid="insp-turn-ballpark-chip"
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white font-black uppercase tracking-wider text-[10px] ${tone} hover:brightness-110`}
        title={`Rough turn ballpark · ${bp.rated} rated / ${bp.unrated} unrated`}>
        <span>Turn ≈ {bp.bucket}</span>
        <span className={`${confTone} normal-case font-bold tracking-normal ml-1 opacity-90`}>·&nbsp;{bp.confidence}</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 w-64 p-3 bg-white text-slate-900 border-2 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded"
          data-testid="insp-turn-ballpark-detail">
          <div className="flex items-center justify-between mb-2">
            <div className="font-black uppercase text-[11px] tracking-widest">Turn ballpark</div>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900 text-lg leading-none">×</button>
          </div>
          <div className="text-2xl font-black text-[#0f172a]" data-testid="insp-turn-ballpark-total">${bp.total.toLocaleString()}</div>
          <div className="text-[11px] text-slate-500 mb-2">
            {bp.rated} rated · {bp.unrated} unrated · {bp.confidence} confidence
          </div>
          {bp.worst.length > 0 ? (
            <div className="space-y-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Top drivers</div>
              {bp.worst.map((w, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="truncate">{w.room} <span className="text-slate-400">· {w.condition}</span></span>
                  <span className="font-bold">${w.cost}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-500">All rated rooms look clean — $0 estimated.</div>
          )}
          <div className="text-[10px] text-slate-400 mt-2 italic">
            Heuristic only — project estimator fills in actuals.
          </div>
        </div>
      )}
    </div>
  );
}

